import { Signal, WritableSignal, computed, signal } from '@angular/core';

import { TransformerInterface } from '@gfe/core/models/entities/entity-visualization-interface';
import { isCsvCompatible } from '@gfe/core/utils/json-schema-utils';
import { isNil } from '@gfe/core/utils/shared-utils';
import { ModalConfig, ModalType } from '@gfe/shared/components/modal/modal.component';
import { QualifiersConfig } from '@gfe/shared/components/search-query-builder/search-query-builder.component';
import { SearchConfig } from '@gfe/shared/components/toolbox/toolbox.component';
import { UploadZoneConfig } from '@gfe/shared/components/upload-zone/upload-zone.component';

import { EntityComponent } from './entity.component';
import { csvExportModelNames } from './transformer-selection.utils';

interface DeleteEntitiesModalCfg {
  isOpen: boolean;
  config: ModalConfig;
  title: Signal<string>;
}

export function buildDeleteEntitiesModal<T>(
  entityComponent: EntityComponent<T>,
  context: 'data' | 'plan' = 'data'
): WritableSignal<DeleteEntitiesModalCfg> {
  const deleteEntitiesModal = signal<DeleteEntitiesModalCfg>({
    isOpen: false,
    config: {
      dismissible: true,
      askingModal: true,
      closeOnBackdropClick: true,
      icon: 'warning',
      type: ModalType.ERROR,
      actions: [
        {
          label: entityComponent.translate.instant('common.cancel'),
          color: 'cancel',
          callback: () => {
            deleteEntitiesModal.set({
              ...deleteEntitiesModal(),
              isOpen: false
            });
          }
        },
        {
          label: entityComponent.translate.instant('common.submit'),
          icon: 'trash',
          callback: () => {
            entityComponent.onDeleteEntities();
            deleteEntitiesModal.set({
              ...deleteEntitiesModal(),
              isOpen: false
            });
          },
          color: 'danger'
        }
      ]
    },
    title: computed(() =>
      entityComponent.translate.instant(
        context === 'data'
          ? 'features.entities.delete_modal.title'
          : 'features.plan.remove_data.title'
      )
    )
  });
  return deleteEntitiesModal;
}

interface AddToPlanModalCfg {
  isOpen: boolean;
  config: ModalConfig;
  title: Signal<string>;
}

export function buildAddToPlanModal<T>(
  entityComponent: EntityComponent<T>
): WritableSignal<AddToPlanModalCfg> {
  const addToPlanModal = signal<AddToPlanModalCfg>({
    isOpen: false,
    config: {
      dismissible: true,
      askingModal: true,
      closeOnBackdropClick: true,
      icon: 'download',
      type: ModalType.INFO,
      actions: [
        {
          label: entityComponent.translate.instant('common.cancel'),
          color: 'cancel',
          callback: () => {
            addToPlanModal.set({ ...addToPlanModal(), isOpen: false });
          }
        },
        {
          label: entityComponent.translate.instant('common.submit'),
          icon: 'download',
          callback: () => {
            entityComponent.confirmAddToPlan();
            addToPlanModal.set({ ...addToPlanModal(), isOpen: false });
          },
          color: 'notice'
        }
      ]
    },
    title: computed(() => entityComponent.translate.instant('features.plan.add_data.title'))
  });
  return addToPlanModal;
}

export function buildSearchConfig<T>(
  entityComponent: EntityComponent<T>
): Signal<SearchConfig> {
  return computed<SearchConfig>(() => ({
    active: true,
    type: 'advanced',
    shortcut: true,
    qualifiers: entityComponent.columns().reduce((acc, col) => {
      const filterCfg = entityComponent.filterableColMap()[col.key];
      if (!isNil(filterCfg)) {
        const translatedLabel =
          entityComponent.entityLabelsTranslationService.translateSearchFilterLabel(
            filterCfg.translationLabel
          );

        acc[translatedLabel] = {
          type: 'text', // Need to be enum if we know the possibles values
          description: '',
          supportsRegex: true
        };
      }
      return acc;
    }, {} as QualifiersConfig)
  }));
}

export function buildUploadZoneConfig<T>(
  entityComponent: EntityComponent<T>
): Signal<UploadZoneConfig> {
  return computed<UploadZoneConfig>(() => {
    const transformerNames = entityComponent
      .currentEntityTransformers()
      .map((transformer: TransformerInterface) => {
        const rawTransformerName = transformer.transformerDisplayLabel();
        return entityComponent.entityLabelsTranslationService.translateCsvTransformerName(
          rawTransformerName
        );
      });
    return {
      displayText: entityComponent.translate.instant('upload_zone.file_drop.message'),
      acceptedFileTypesText: entityComponent.translate.instant(
        'upload_zone.file_drop.accepted_file_types'
      ),
      acceptedFileTypes: ['.csv', 'text/csv'],
      multiple: false,
      onlyCaptureFiles: false,
      availableTransformers: transformerNames
    };
  });
}

export function buildEditionTransformerSchema<T>(
  entityComponent: EntityComponent<T>
): Signal<TransformerInterface | null> {
  return computed<TransformerInterface | null>(() => {
    const transformers = entityComponent.allTransformers();
    const entityType = entityComponent.entityType().toUpperCase();
    const entityVersionName = entityComponent.entityVersionName();

    const matchingTransformer = transformers.filter((t: TransformerInterface) => {
      return (
        t.transformerInfo.transformer.name === entityVersionName &&
        t.transformerInfo.transformer.generatedEntities.length === 1 &&
        t.transformerInfo.transformer.generatedEntities.includes(entityType) &&
        t.transformerInfo.dataModels.length > 0
      );
    });
    return matchingTransformer[0] || null;
  });
}

export function buildCurrentEntityTransformers<T>(
  entityComponent: EntityComponent<T>
): Signal<TransformerInterface[]> {
  return computed<TransformerInterface[]>(() => {
    const allTransformers = entityComponent.allTransformers();
    const entityType = entityComponent.entityType().toUpperCase();
    return allTransformers.filter((t: TransformerInterface) => {
      return (
        t.transformerInfo.transformer.generatedEntities.includes(entityType) &&
        t.transformerInfo.dataModels.length > 0 &&
        isCsvCompatible(t.transformerInfo.dataModels[0].metadata.schema)
      );
    });
  });
}

export function buildCurrentCsvEntityModeNames<T>(
  entityComponent: EntityComponent<T>
): Signal<string[]> {
  return computed<string[]>(() =>
    csvExportModelNames(
      entityComponent.allTransformers(),
      entityComponent.entityType().toUpperCase()
    )
  );
}
